import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { BaseClientFormComponent } from 'components/client-view-sections/base-client-form';
import { ApiService } from 'services/api.service';
import { ToastService } from 'services/toast.service';
import { CreateLegalEntityRequest, LegalEntityCard, LegalEntityFormOption, UpdateLegalEntityRequest } from 'types/legal-entity.interface';
import { CountryDto } from 'types/country.interface';
import { formatDatetime } from 'util/date.utility';
import {
  LENGTH_LV_REGISTRATION_NUMBER,
  MAX_LENGTH_LEGAL_ADDRESS,
  MAX_LENGTH_COMPANY_REGISTRATION_NUMBER,
  MAX_LENGTH_EMAIL,
  MAX_LENGTH_NAME,
  MAX_LENGTH_PHONE,
  MIN_LENGTH_ADDRESS,
  MIN_LENGTH_EMAIL,
  MIN_LENGTH_PHONE,
  emailValidator,
  getControlsWithErrors,
  latvianRegistrationNumberValidator,
  phoneValidator
} from 'util/form.utility';

@Component({
  selector: 'app-organization-general-info-form',
  standalone: true,
  imports: [TranslateModule, ReactiveFormsModule, NgClass, NgTemplateOutlet, SpinnerComponent, ConfirmModalComponent],
  templateUrl: './organization-general-info-form.component.html',
  styleUrls: ['../../form/base-form.scss', './organization-general-info-form.component.scss']
})
export class OrganizationGeneralInfoFormComponent extends BaseClientFormComponent implements OnInit, OnChanges {
  public readonly formatDatetime = formatDatetime;

  legalEntityFormOptions: LegalEntityFormOption[] = [];

  @Input() mode: 'add' | 'edit' = 'edit';
  @Input() legalEntity: LegalEntityCard | null = null;
  @Input() countries: CountryDto[] = [];
  @Output() legalEntityCreated = new EventEmitter<LegalEntityCard>();
  @Output() legalEntityUpdated = new EventEmitter<LegalEntityCard>();
  @Output() formChanged = new EventEmitter<boolean>();

  isLoading: boolean = false;
  isConfirmModalOpen: boolean = false;
  hasFormChanged: boolean = false;
  registrationNumberMaxLength: number = MAX_LENGTH_COMPANY_REGISTRATION_NUMBER;
  private formSub?: Subscription;
  private countrySubscription?: Subscription;

  get isAddMode(): boolean {
    return this.mode === 'add';
  }

  get isNationalId(): boolean {
    return !this.isAddMode && this.legalEntity?.authenticationType === 'NationalId';
  }

  get countryId(): AbstractControl<any, any> | null {
    return this.form.get('countryId');
  }
  get registrationNumber(): AbstractControl<any, any> | null {
    return this.form.get('registrationNumber');
  }
  get name(): AbstractControl<any, any> | null {
    return this.form.get('name');
  }
  get type(): AbstractControl<any, any> | null {
    return this.form.get('type');
  }
  get email(): AbstractControl<any, any> | null {
    return this.form.get('email');
  }
  get phone(): AbstractControl<any, any> | null {
    return this.form.get('phone');
  }
  get legalAddress(): AbstractControl<any, any> | null {
    return this.form.get('legalAddress');
  }

  constructor(
    protected override formBuilder: FormBuilder,
    protected override apiService: ApiService,
    private toastService: ToastService
  ) {
    super(formBuilder, apiService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.apiService.getLegalEntityForms().subscribe({
      next: (options) => { this.legalEntityFormOptions = options; },
      error: (err) => console.error(err)
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['countries'] && this.isAddMode && this.countries.length > 0) {
      const latviaId = this.countries.find((c) => c.code === 'LV')?.id;
      if (latviaId && this.form.get('countryId')?.value === -1) {
        this.form.get('countryId')?.setValue(latviaId);
      }
    }
  }

  private subscribeToCountryChanges(): void {
    this.countrySubscription?.unsubscribe();
    this.countrySubscription = this.form.get('countryId')?.valueChanges.subscribe((countryId: string) => {
      this.updateRegistrationNumberValidators(countryId);
    });
  }

  private updateRegistrationNumberValidators(countryId: string): void {
    const latviaId = this.countries.find((c) => c.code === 'LV')?.id;
    const isLatvia = countryId === latviaId;
    const registrationNumberControl = this.form.get('registrationNumber');

    if (isLatvia) {
      this.registrationNumberMaxLength = LENGTH_LV_REGISTRATION_NUMBER;
      registrationNumberControl?.setValidators([Validators.required, latvianRegistrationNumberValidator()]);
    } else {
      this.registrationNumberMaxLength = MAX_LENGTH_COMPANY_REGISTRATION_NUMBER;
      registrationNumberControl?.setValidators([Validators.required, Validators.maxLength(MAX_LENGTH_COMPANY_REGISTRATION_NUMBER)]);
    }

    registrationNumberControl?.updateValueAndValidity();
  }

  private bindFormDirtyWatcher(): void {
    this.formSub?.unsubscribe();
    this.formSub = this.form.valueChanges.subscribe(() => {
      this.hasFormChanged = this.form.dirty;
      this.formChanged.emit(this.form.dirty);
    });
  }

  override setIsEditMode(isEditMode: boolean, confirmed: boolean = false): void {
    if (!confirmed && !isEditMode) {
      this.isConfirmModalOpen = true;
    } else {
      super.setIsEditMode(isEditMode);
      if (!isEditMode) {
        this.fillForm();
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.formChanged.emit(false);
      }
    }
  }

  override fillForm(): void {
    const latviaId = this.countries.find((c) => c.code === 'LV')?.id;

    if (this.isAddMode) {
      this.registrationNumberMaxLength = LENGTH_LV_REGISTRATION_NUMBER;
      this.form = this.formBuilder.group({
        countryId: [latviaId || -1, [Validators.required, Validators.min(0)]],
        registrationNumber: ['', [Validators.required, latvianRegistrationNumberValidator()]],
        name: ['', [Validators.required, Validators.maxLength(MAX_LENGTH_NAME)]],
        type: ['', [Validators.required]],
        email: ['', [emailValidator(), Validators.minLength(MIN_LENGTH_EMAIL), Validators.maxLength(MAX_LENGTH_EMAIL)]],
        phone: ['', [phoneValidator(), Validators.minLength(MIN_LENGTH_PHONE), Validators.maxLength(MAX_LENGTH_PHONE)]],
        legalAddress: ['', [Validators.minLength(MIN_LENGTH_ADDRESS), Validators.maxLength(MAX_LENGTH_LEGAL_ADDRESS)]]
      });
    } else {
      const isLatvia = this.legalEntity?.country?.id === latviaId;
      this.registrationNumberMaxLength = isLatvia ? LENGTH_LV_REGISTRATION_NUMBER : MAX_LENGTH_COMPANY_REGISTRATION_NUMBER;

      this.form = this.formBuilder.group({
        countryId: [this.legalEntity?.country?.id || -1, [Validators.required, Validators.min(0)]],
        registrationNumber: [
          this.legalEntity?.registrationNumber || '',
          isLatvia
            ? [Validators.required, latvianRegistrationNumberValidator()]
            : [Validators.required, Validators.maxLength(MAX_LENGTH_COMPANY_REGISTRATION_NUMBER)]
        ],
        name: [this.legalEntity?.name || '', [Validators.required, Validators.maxLength(MAX_LENGTH_NAME)]],
        type: [this.legalEntity?.type || '', [Validators.required]],
        email: [
          this.legalEntity?.email || '',
          [emailValidator(), Validators.minLength(MIN_LENGTH_EMAIL), Validators.maxLength(MAX_LENGTH_EMAIL)]
        ],
        phone: [
          this.legalEntity?.phone || '',
          [phoneValidator(), Validators.minLength(MIN_LENGTH_PHONE), Validators.maxLength(MAX_LENGTH_PHONE)]
        ],
        legalAddress: [
          this.legalEntity?.legalAddress || '',
          [Validators.minLength(MIN_LENGTH_ADDRESS), Validators.maxLength(MAX_LENGTH_LEGAL_ADDRESS)]
        ]
      });
    }

    if (!this.isAddMode && this.legalEntity?.authenticationType === 'NationalId') {
      this.form.get('countryId')?.disable();
      this.form.get('registrationNumber')?.disable();
      this.form.get('name')?.disable();
    }

    this.bindFormDirtyWatcher();
    this.subscribeToCountryChanges();
  }

  onCountryBlur(): void {
    const countryIdControl = this.form.get('countryId');
    if (countryIdControl) {
      const value = countryIdControl.value;
      countryIdControl.setValue(value === '' ? null : value);
    }
  }

  getLegalEntityFormLabel(): string {
    const option = this.legalEntityFormOptions.find((o) => o.code === this.legalEntity?.type);
    return option ? option.name : '';
  }

  override shouldRenderError(key: string): boolean {
    if (this.isAddMode && this.submitAttempted) {
      return true;
    }
    return super.shouldRenderError(key);
  }

  override submit(): void {
    this.submitAttempted = true;
    this.form.markAllAsTouched();

    if (this.form.valid) {
      this.isLoading = true;

      if (this.isAddMode) {
        const args: CreateLegalEntityRequest = {
          registrationNumber: this.registrationNumber?.value,
          name: this.name?.value,
          type: this.type?.value,
          legalAddress: this.legalAddress?.value,
          email: this.email?.value || null,
          phone: this.phone?.value || null,
          countryId: this.countryId?.value === -1 ? null : this.countryId?.value
        };

        this.apiService.createLegalEntity(args).subscribe(
          (legalEntity: LegalEntityCard) => {
            this.isLoading = false;
            this.toastService.showSuccess('addOrganizationSuccess', '', { shouldTranslate: true });
            this.legalEntityCreated.emit(legalEntity);
          },
          (error: any) => {
            this.isLoading = false;
            const errors = error?.error?.errors as Record<string, string[] | undefined> | undefined;
            if (errors?.['RegistrationNumber']?.length || errors?.['registrationNumber']?.length) {
              this.toastService.showDanger('organizationRegistrationNumberExists', '', { shouldTranslate: true });
            } else {
              this.toastService.showRequestError();
            }
          }
        );
      } else {
        const args: UpdateLegalEntityRequest = {
          id: this.legalEntity!.id,
          registrationNumber: this.registrationNumber?.value,
          name: this.name?.value,
          type: this.type?.value,
          legalAddress: this.legalAddress?.value,
          email: this.email?.value || null,
          phone: this.phone?.value || null,
          countryId: this.countryId?.value === -1 ? null : this.countryId?.value,
          verifiedById: this.legalEntity!.verifiedBy?.id || null
        };

        this.apiService.updateLegalEntity(args).subscribe(
          (legalEntity: LegalEntityCard) => {
            this.legalEntity = legalEntity;
            this.submitAttempted = false;
            this.isLoading = false;
            this.isEditMode = false;
            this.toastService.showSuccess('submitOrganizationSuccess', '', {
              shouldTranslate: true
            });
            this.hasFormChanged = false;
            this.formChanged.emit(false);
            this.legalEntityUpdated.emit(legalEntity);
          },
          (error: any) => {
            this.isLoading = false;
            try {
              const regNumError = error.error?.errors?.['registrationNumber']?.[0]?.trim().toLowerCase();
              if (regNumError?.includes('already')) {
                this.toastService.showDanger('organizationRegistrationNumberExists', '', {
                  shouldTranslate: true
                });
              } else {
                this.toastService.showRequestError();
              }
            } catch {
              this.toastService.showRequestError();
            }
            console.error(error);
          }
        );
      }
    } else {
      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'pages.organization');
    }
  }
}
